import axios from 'axios'
import globalConfig from '../config/globalConfig'
import { getDevTenant } from '../config/devTenant'

// This allows us to resend the cookies that come from the server on each request with AXIOS
axios.defaults.withCredentials = true

/**
 * Axios instance with global configuration for making HTTP requests.
 * @type {import('axios').AxiosInstance}
 * @alias request
 * @namespace Services
 * @property {Function} interceptors - Axios interceptors for request and response.
 */

const request = axios.create({
  baseURL: globalConfig.axios.apiUrl,
  timeout: globalConfig.axios.requestTimeout
})

/**
 * Axios request interceptor to perform actions before sending a request.
 * @function
 * @memberof request
 * @param {Object} config - Axios request configuration.
 * @returns {Object} Modified Axios request configuration.
 * @throws {Error} If an error occurs during request interception.
 */
request.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    // TODO: The language should be loaded dynamically based on user preference
    config.headers['accept-language'] = 'en'
    // MF-560 — local development only: which school this local app acts as (see config/devTenant).
    // import.meta.env.DEV is false in every build → Vite drops this block entirely.
    if (import.meta.env.DEV) {
      const devTenant = getDevTenant()
      if (devTenant) config.headers['X-Dev-Tenant-Domain'] = devTenant
    }
    config.signal = new AbortController().signal
    config.params = { ...config.params }

    return config
  },
  (error) => Promise.reject(error)
)

/**
 * Axios response interceptor to extract the data property from the response.
 * @function
 * @memberof request
 * @param {Object} response - Axios response object.
 * @returns {Object} Data property of the response.
 * @throws {Error} If an error occurs during response interception.
 */

request.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config
    const status = error.response?.status

    // MF-497: un 401 puede ser transitorio (p.ej. el BE reconstruye la sesión L2
    // en la misma request — self-heal). Reintentar UNA sola vez en silencio antes
    // de destruir la sesión. Seguro incluso para POST/PUT: un 401 lo lanza el
    // middleware ANTES del handler, así que la operación no llegó a ejecutarse.
    // Solo con sesión establecida (hay accessToken) para no reintentar el login.
    if (status === 401 && originalRequest && !originalRequest._retry && localStorage.getItem('accessToken')) {
      originalRequest._retry = true
      return request(originalRequest)
    }

    if (status === 401) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('userSession')

      //This avoid login page to be reloaded when login fails
      if (window.location.pathname != '/login') {
        window.location.href = '/login'
      }
    }

    if (error.response?.status <= 200 || error.response?.status >= 299) {
      const newError = new Error(error.response?.data.error.message)
      newError.name = error.response?.data.error.code
      newError.code = error.response?.data.error.code
      newError.status = error.response?.status
      throw newError
    }

    return error?.response?.data
  }
)

export default request
